import React from 'react';
import {
  Drawer,
  Box,
  Button,
  Typography,
  IconButton,
  Divider,
} from '@mui/material';
import HubIcon from '@mui/icons-material/Hub';
import { alpha, useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

import { FixedRoutesContent } from './FixedRoutesContent';
import { useNetworkBrowse } from '../../../context/NetworkBrowseContext';
import { liftangoBrandTokens } from '../../../theme';
import { frostedDrawerPaperSx } from '../../../theme/componentStyles';
import {
  FIXED_ROUTES_RESIZE_HANDLE_WIDTH,
  useFixedBottomDrawerResize,
} from '../../../hooks/useFixedBottomDrawerResize';
import { MAP_UI_RIGHT_RAIL_WIDTH_PX, MAP_UI_TOP_BAR_HEIGHT_PX } from '../mapUiLayout';

import { Icon } from '@liftango/ui-icons';
interface FixedRoutesDrawerProps {
  open: boolean;
  onClose: () => void;
  selectedRouteId?: string | null;
}

export const FixedRoutesDrawer: React.FC<FixedRoutesDrawerProps> = ({ open, onClose, selectedRouteId }) => {
  const { t } = useTranslation('map');
  const shell = liftangoBrandTokens.fixedRoutes;
  const theme = useTheme();
  const { setPickerOpen } = useNetworkBrowse();
  const { widthPx, isResizing, handleResizeMouseDown, handleResizeTouchStart, nudgeWidth } =
    useFixedBottomDrawerResize();

  const handleResizeKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const step = event.shiftKey ? 80 : 24;
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      nudgeWidth(step);
      return;
    }
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      nudgeWidth(-step);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose} // Handles backdrop click and ESC key to dismiss
      variant="persistent"
      sx={{
        zIndex: (t) => t.zIndex.drawer,
      }}
      PaperProps={{
        sx: (t) => ({
          width: `${widthPx}px`,
          // Offset left of the right rail (rail width + 1px), matching the other
          // map drawers, and clamp so a resized-wide drawer never slides under it.
          maxWidth: `calc(100vw - ${MAP_UI_RIGHT_RAIL_WIDTH_PX}px)`,
          height: `calc(100vh - ${MAP_UI_TOP_BAR_HEIGHT_PX}px)`,
          top: MAP_UI_TOP_BAR_HEIGHT_PX,
          right: MAP_UI_RIGHT_RAIL_WIDTH_PX,
          p: 0,
          display: 'flex',
          flexDirection: 'row',
          boxSizing: 'border-box',
          zIndex: t.zIndex.drawer,
          borderTopLeftRadius: 16,
          borderBottomLeftRadius: 16,
          ...frostedDrawerPaperSx(t, 0.30),
          border: `1px solid ${shell.drawerBorder}`,
          borderRight: 'none',
          boxShadow: '-6px 0 24px rgba(15, 23, 42, 0.12)',
          transition: isResizing ? 'none' : 'width 0.25s ease, max-width 0.25s ease',
        }),
      }}
    >
      <Box
        onMouseDown={handleResizeMouseDown}
        onTouchStart={handleResizeTouchStart}
        onKeyDown={handleResizeKeyDown}
        role="separator"
        aria-orientation="vertical"
        aria-label={t('fixedRoutes.resizeAria')}
        tabIndex={0}
        sx={{
          width: FIXED_ROUTES_RESIZE_HANDLE_WIDTH,
          minWidth: FIXED_ROUTES_RESIZE_HANDLE_WIDTH,
          cursor: 'ew-resize',
          touchAction: 'none',
          flexShrink: 0,
          backgroundColor: alpha('#ffffff', 0.08),
          '&:hover': {
            backgroundColor: alpha('#ffffff', 0.15),
          },
          transition: isResizing ? 'none' : theme.transitions.create('background-color', { duration: 150 }),
        }}
      />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,
          flex: 1,
          // The persistent Drawer renders inline inside `.App`, which sets
          // `text-align: center` (CRA default). Reset it so drawer text reads
          // left-aligned; intentional empty-states set their own centering.
          textAlign: 'left',
        }}
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          sx={{ pl: 2, pr: 3, py: 2.25, flexShrink: 0 }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: shell.headerText,
              fontSize: '1.4rem',
            }}
          >
            {t('fixedRoutes.drawerTitle')}
          </Typography>
          <Box display="flex" alignItems="center" gap={1}>
            <Button
              onClick={() => setPickerOpen(true)}
              startIcon={<HubIcon />}
              size="small"
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                color: shell.headerText,
                border: `1px solid ${shell.tabBorder}`,
                borderRadius: 1.5,
                px: 1.25,
                '&:hover': { backgroundColor: shell.tabBackground },
              }}
            >
              {t('sidebar.openTransitNetworks')}
            </Button>
            <IconButton
              onClick={onClose}
              aria-label={t('fixedRoutes.closeDrawerAria')}
              sx={{
                color: shell.subText,
                '&:hover': {
                  backgroundColor: shell.tabBackground,
                },
              }}
            >
              <Icon size={20} name="xmark" />
            </IconButton>
          </Box>
        </Box>

        <Divider sx={{ borderColor: shell.tabBorder, flexShrink: 0 }} />

        <Box
          flex={1}
          minHeight={0}
          overflow="auto"
          sx={{
            overflowY: 'auto',
            overflowX: 'hidden',
            px: 2,
            py: 2,
            backgroundColor: 'rgba(248, 250, 252, 0.16)',
            backdropFilter: 'blur(8px) saturate(110%)',
            WebkitBackdropFilter: 'blur(8px) saturate(110%)',
          }}
        >
          <FixedRoutesContent onClose={onClose} selectedRouteId={selectedRouteId} isDrawerOpen={open} />
        </Box>
      </Box>
    </Drawer>
  );
};

export default FixedRoutesDrawer;
